import { ApiError } from "@/shared/api/api-error";

export type TokenProvider = () => Promise<string | null> | string | null;

export interface HttpClientOptions {
  baseUrl: string;
  getToken?: TokenProvider;
}

export interface BaseUrlValidationOptions {
  allowHttp?: boolean;
}

export function normalizeApiBaseUrl(
  baseUrl: string,
  { allowHttp = false }: BaseUrlValidationOptions = {}
): string {
  const trimmed = baseUrl.trim();

  if (!trimmed) {
    throw new Error("API base URL is required.");
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    throw new Error(`Invalid API base URL: "${baseUrl}"`);
  }

  if (url.protocol !== "https:" && !(allowHttp && url.protocol === "http:")) {
    throw new Error(`Invalid API base URL protocol: "${url.protocol}". Expected https:.`);
  }

  url.pathname = url.pathname.replace(/\/+$/, "");
  url.search = "";
  url.hash = "";

  return url.toString().replace(/\/$/, "");
}

export interface HttpRequestOptions {
  signal?: AbortSignal;
}

export interface BinaryHttpResponse {
  blob: Blob;
  contentDisposition: string | null;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly getToken?: TokenProvider;

  constructor({ baseUrl, getToken }: HttpClientOptions) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.getToken = getToken;
  }

  get<T>(path: string, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>("GET", path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>("POST", path, body, options);
  }

  postBinary(path: string, body?: unknown, options?: HttpRequestOptions): Promise<ArrayBuffer> {
    return this.requestBinary(path, body, options);
  }

  getBinary(path: string, options?: HttpRequestOptions): Promise<BinaryHttpResponse> {
    return this.requestDownload(path, options);
  }

  put<T>(path: string, body?: unknown, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>("PUT", path, body, options);
  }

  delete<T>(path: string, options?: HttpRequestOptions): Promise<T> {
    return this.request<T>("DELETE", path, undefined, options);
  }

  private async requestBinary(
    path: string,
    body?: unknown,
    options?: HttpRequestOptions
  ): Promise<ArrayBuffer> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/x-protobuf",
    };

    const token = await this.getToken?.();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${normalizePath(path)}`, {
      method: "POST",
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options?.signal,
    }).catch((error: unknown) => {
      throw mapNetworkError(error);
    });

    if (!response.ok) {
      const errorBody = await readJsonBody(response);
      throw ApiError.fromResponse(response.status, errorBody);
    }

    const contentType = response.headers.get("content-type")?.split(";", 1)[0].trim().toLowerCase();
    if (contentType !== "application/x-protobuf") {
      throw new Error(
        `Unexpected binary response content type: ${contentType ?? "missing"}. Expected application/x-protobuf.`
      );
    }

    return response.arrayBuffer();
  }

  private async requestDownload(
    path: string,
    options?: HttpRequestOptions
  ): Promise<BinaryHttpResponse> {
    const headers: Record<string, string> = {
      Accept: "application/octet-stream, application/zip;q=0.9, */*;q=0.8",
    };

    const token = await this.getToken?.();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${normalizePath(path)}`, {
      method: "GET",
      headers,
      signal: options?.signal,
    }).catch((error: unknown) => {
      throw mapNetworkError(error);
    });

    if (!response.ok) {
      const errorBody = await readJsonBody(response);
      throw ApiError.fromResponse(response.status, errorBody);
    }

    return {
      blob: await response.blob(),
      contentDisposition: response.headers.get("content-disposition"),
    };
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: HttpRequestOptions
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const token = await this.getToken?.();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${normalizePath(path)}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options?.signal,
    }).catch((error: unknown) => {
      throw mapNetworkError(error);
    });

    if (!response.ok) {
      const errorBody = await readJsonBody(response);
      throw ApiError.fromResponse(response.status, errorBody);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  return new HttpClient(options);
}

function normalizePath(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

async function readJsonBody(response: Response): Promise<unknown> {
  return response.json().catch(() => ({}));
}

function mapNetworkError(error: unknown): Error {
  if (error instanceof DOMException && error.name === "AbortError") return error;
  if (error instanceof Error && error.name === "AbortError") return error;

  if (error instanceof TypeError && error.message.includes("Failed to fetch")) {
    return new ApiError("Cannot connect to API. This may be a CORS configuration issue.", 0, {
      type: "NETWORK_ERROR",
      title: "Network Error",
      status: 0,
      detail: "Cannot connect to API. This may be a CORS configuration issue.",
    });
  }

  return new ApiError(error instanceof Error ? error.message : "Network request failed", 0, {
    type: "NETWORK_ERROR",
    title: "Network Error",
    status: 0,
    detail: error instanceof Error ? error.message : "Network request failed",
  });
}
